import { functionAppScope } from "../authConfig";
import { msalInstance } from "../index";
import {
  InteractionRequiredAuthError,
  AccountInfo,
  BrowserAuthError,
} from "@azure/msal-browser";
const apiUrl = process.env.REACT_APP_API_URL;

let lastTimeoutError = 0;
const TIMEOUT_ERROR_COOLDOWN = 10000;
let isRedirecting = false;

function fetchAccessToken(forceRefresh = false) {
  const account = msalInstance.getAllAccounts();

  if (
    !localStorage.getItem("loggedInUserEmail") ||
    !localStorage.getItem("loggedInUserFullName")
  ) {
    if (account.length > 0 && account[0]) {
      const loggedInUser = account[0];
      if (loggedInUser?.username && loggedInUser?.name) {
        if (!localStorage.getItem("loggedInUserEmail")) {
          localStorage.setItem("loggedInUserEmail", loggedInUser.username);
        }
        if (!localStorage.getItem("loggedInUserFullName")) {
          localStorage.setItem("loggedInUserFullName", loggedInUser.name);
        }
      }
    }
  }

  return new Promise((resolve, reject) => {
    if (account.length === 0) {
      reject(new Error("No accounts found"));
      return;
    }

    const now = Date.now();
    if (now - lastTimeoutError < TIMEOUT_ERROR_COOLDOWN) {
      console.warn("Skipping token acquisition due to recent timeout error");
      reject(new Error("Token acquisition skipped due to recent timeout"));
      return;
    }

    msalInstance
      .acquireTokenSilent({
        ...functionAppScope,
        account: account[0],
        forceRefresh,
      })
      .then((response) => {
        isRedirecting = false;
        resolve(response.accessToken);
      })
      .catch((e) => {
        if (e instanceof InteractionRequiredAuthError) {
          if (isRedirecting) {
            console.warn(
              "Already redirecting to login, skipping duplicate redirect"
            );
            reject(new Error("Login redirect already in progress"));
            return;
          }

          console.warn(
            "Token refresh requires user interaction. Redirecting to login."
          );
          isRedirecting = true;
          msalInstance.acquireTokenRedirect({
            ...functionAppScope,
            account: msalInstance.getActiveAccount() as AccountInfo,
          });
        } else if (
          e instanceof BrowserAuthError &&
          e.errorCode === "monitor_window_timeout"
        ) {
          lastTimeoutError = Date.now();
          console.error(
            "Token acquisition timeout detected, will retry after cooldown"
          );
          reject(e);
        } else {
          console.error("Token acquisition error:", e);
          reject(e);
        }
      });
  });
}

interface FetchResponse<T = any> {
  data: T;
  status: number;
  headers: Headers;
}

interface FetchError {
  response?: {
    status: number;
    data: any;
    headers: Headers;
  };
  message: string;
  config?: { method: string; url: string };
}

const retryAttempts = new Map<string, number>();
const MAX_RETRY_ATTEMPTS = 1;

class TimeoutError extends Error {
  config: { method: string; url: string };
  constructor(message: string, config: { method: string; url: string }) {
    super(message);
    this.name = 'TimeoutError';
    this.config = config;
  }
}

async function handleResponseError(
  response: Response,
  method: string,
  url: string,
  requestFn: () => Promise<FetchResponse>
): Promise<FetchResponse> {
  let responseData: any;
  try {
    responseData = await response.json();
  } catch {
    responseData = null;
  }

  if (response.status === 401) {
    const requestKey = `${method}-${url}`;
    const attempts = retryAttempts.get(requestKey) || 0;

    if (attempts < MAX_RETRY_ATTEMPTS) {
      retryAttempts.set(requestKey, attempts + 1);
      console.warn(
        "Received 401 error. Attempting to refresh token and retry..."
      );

      try {
        await fetchAccessToken(true);
        retryAttempts.delete(requestKey);
        return requestFn();
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        retryAttempts.delete(requestKey);

        if (!isRedirecting) {
          console.warn("Unable to refresh token. Redirecting to login.");
          isRedirecting = true;
          msalInstance.loginRedirect();
        }

        throw refreshError;
      }
    } else {
      retryAttempts.delete(requestKey);

      console.error(
        "Max token refresh attempts exceeded. Redirecting to login."
      );

      if (!isRedirecting) {
        isRedirecting = true;
        msalInstance.loginRedirect();
      }
    }
  }

  if (response.status === 403) {
    const accounts = msalInstance.getAllAccounts();
    const isAuthenticated = accounts.length > 0;

    if (isAuthenticated) {
      console.warn(
        "User is authenticated but received 403 Forbidden. User may not have access to this resource."
      );
    } else {
      if (!isRedirecting) {
        isRedirecting = true;
        msalInstance.loginRedirect();
      }
    }
  }

  const error: FetchError = {
    response: {
      status: response.status,
      data: responseData,
      headers: response.headers,
    },
    message: responseData?.message || responseData?.title || `Request failed with status ${response.status}`,
    config: { method, url },
  };
  throw error;
}

const DEFAULT_TIMEOUT_MS = 30000;

async function request<T = any>(
  method: string,
  url: string,
  data?: any,
  config?: { data?: any; headers?: Record<string, string | null>; responseType?: string; timeout?: number }
): Promise<FetchResponse<T>> {
  const roleType = localStorage.getItem("selectedRole") || "";
  const username = localStorage.getItem("username") || "";
  const fullUrl = `${apiUrl}${url}`;

  const body = data ?? config?.data;
  const timeoutMs = config?.timeout ?? DEFAULT_TIMEOUT_MS;

  const doRequest = async (): Promise<FetchResponse<T>> => {
    const accessToken = await fetchAccessToken();
    const isFormData = body instanceof FormData;

    const callerHeaders = Object.fromEntries(
      Object.entries(config?.headers || {}).filter(([, v]) => v !== null)
    ) as Record<string, string>;

    const headers: Record<string, string> = {
      ...(!isFormData ? { "Content-Type": "application/json" } : {}),
      ...callerHeaders,
    };

    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      headers["X-MS-TOKEN-AAD-ACCESS-TOKEN"] = `Bearer ${accessToken}`;
      headers["X-MS-TOKEN-AAD-ID-TOKEN"] = `Bearer ${accessToken}`;
      headers["X-MS-TOKEN-AAD-EXPIRES-ON"] = `Bearer ${accessToken}`;
      headers["X-MS-TOKEN-AAD-REFRESH-TOKEN"] = `Bearer ${accessToken}`;
      headers["RoleType"] = roleType;
      headers["Username"] = username;            
      headers["selectedPAType"] = localStorage.getItem("pa.selectedPAType") || "sold";
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const fetchOptions: RequestInit = {
      method: method.toUpperCase(),
      headers,
      signal: controller.signal,
      ...(body !== undefined ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    };

    let response: Response;
    try {
      response = await fetch(fullUrl, fetchOptions);
    } catch (e: any) {
      if (e?.name === "AbortError") {
        throw new TimeoutError(`Request timed out after ${timeoutMs}ms`, { method, url });
      }
      throw e;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      return handleResponseError(response, method, url, doRequest);
    }

    let responseData: T;
    const contentType = response.headers.get("content-type");
    if (config?.responseType === "blob") {
      responseData = await response.blob() as unknown as T;
    } else if (contentType && contentType.includes("application/json")) {
      responseData = await response.json();
    } else {
      const text = await response.text();
      try {
        responseData = JSON.parse(text);
      } catch {
        responseData = text as unknown as T;
      }
    }

    return {
      data: responseData,
      status: response.status,
      headers: response.headers,
    };
  };

  return doRequest();
}

const httpClient = {
  get<T = any>(url: string, config?: { params?: Record<string, any>; headers?: Record<string, string | null>; responseType?: string; timeout?: number }) {
    const fullUrl = config?.params
      ? `${url}?${new URLSearchParams(
        Object.entries(config.params)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => [k, String(v)])
      ).toString()}`
      : url;
    return request<T>("GET", fullUrl, undefined, { headers: config?.headers, responseType: config?.responseType, timeout: config?.timeout });
  },
  post<T = any>(url: string, data?: any, config?: { headers?: Record<string, string | null>; responseType?: string; timeout?: number }) {
    return request<T>("POST", url, data, config);
  },
  put<T = any>(url: string, data?: any, config?: { headers?: Record<string, string | null>; responseType?: string; timeout?: number }) {
    return request<T>("PUT", url, data, config);
  },
  delete<T = any>(url: string, config?: { data?: any; headers?: Record<string, string | null> }) {
    return request<T>("DELETE", url, undefined, config);
  },
};

export { TimeoutError };
export default httpClient;